// Article-body renderer. KB articles authored in the agent app are stored as
// HTML (the column is still named body_md for historical reasons), so they must
// be rendered with dangerouslySetInnerHTML — but ONLY through the sanitizer-lite
// pass below. Tenant agents are semi-trusted authors, yet a stored-XSS in one
// article would compromise every visitor's portal session, so we strip the
// active-content vectors: <script>/<style-adjacent executable tags>, inline
// event handlers (onclick=...), and javascript: URLs. This is deliberately NOT a
// full sanitizer (no allowlist); it removes the things that execute.
//
// Plain-markdown articles (seed/demo content) don't look like HTML at all; for
// those we fall back to the existing React-element Markdown renderer, which
// never touches innerHTML.

import { useMemo } from "react";
import { Markdown } from "./markdown";

/** Heuristic: treat content as HTML when it leads with a tag. */
function looksLikeHtml(source: string): boolean {
  return /^\s*</.test(source);
}

/** Sanitizer-lite: strip executable vectors from tenant-authored article HTML. */
export function sanitizeHtml(html: string): string {
  return (
    html
      // <script>…</script> blocks (and any stray opening/closing script tags) —
      // the primary stored-XSS vector.
      .replace(/<script\b[\s\S]*?<\/script\s*>/gi, "")
      .replace(/<\/?script\b[^>]*>/gi, "")
      // Other tags that execute or exfiltrate without user interaction.
      .replace(/<(iframe|object|embed|form|meta|link|base)\b[^>]*>/gi, "")
      .replace(/<\/(iframe|object|embed|form)\s*>/gi, "")
      // Inline event handlers: onclick="…" / onerror='…' / onload=… .
      .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
      // javascript: (and vbscript:) URLs in href/src/etc.
      .replace(/(href|src|xlink:href|action)\s*=\s*(["']?)\s*(javascript|vbscript):[^"'\s>]*\2/gi, "")
  );
}

/** Renders an article body: sanitized HTML when it is HTML, Markdown otherwise. */
export function ArticleBody({ source }: { source: string }) {
  const html = useMemo(
    () => (looksLikeHtml(source) ? sanitizeHtml(source) : null),
    [source],
  );
  if (html === null) return <Markdown source={source} />;
  // Safe-by-construction: `html` has passed sanitizeHtml above. Never feed this
  // component unsanitized strings from any other path.
  return <div className="markdown" dangerouslySetInnerHTML={{ __html: html }} />;
}
